// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const {
  TYPES: modelTypes,
  TYPES_KEYS: modelTypesKeys
} = require('shared/model-types')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const Modal = require('client-v2/common/components/modal')
const InputText = require('client-v2/common/components/input-text-autosize')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const UserModelRecord = require('client-v2/common/models/user/record')
const { CheckRollout } = require('client-v2/common/containers/rollouts')
// Sibling imports
const ShareModalCollectionContent = require('./components/collection-content')
const ShareModalArticleContent = require('./components/article-content')
const ShareModalUserContent = require('./components/user-content')
const ShareModalShareButton = require('./components/share-button')
const ShareModalShareFriends = require('./components/share-friends-smart')
const { socialShareHref } = require('./utils')

const bem = BemEntity('ShareModal')

class ShareModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      copied: false
    }

    // Bind event listeners
    this.onCopy = this.onCopy.bind(this)
    this.onShare = this.onShare.bind(this)
  }

  static getDerivedStateFromProps(props) {
    if (!props.model) {
      return { copied: false }
    }
    return null
  }

  headerText() {
    switch (this.props.model.type) {
      case modelTypes.ARTICLE:
        return 'Share this post'
      case modelTypes.MIX:
        return 'Share this collection'
      default:
        return 'Share'
    }
  }

  // Event handler methods

  onCopy(event) {
    const { model } = this.props
    event.preventDefault()
    const button = event.target
    const input = button.form[0]
    this.props.onCopy({ input, model })
    this.setState(() => ({ copied: true }))
  }

  onShare(event) {
    const { model } = this.props
    const anchor = event.currentTarget
    const href = anchor.getAttribute('href')
    const { shareType } = anchor.dataset
    if (shareType !== 'email') {
      /**
       * `mailto:` links need to be handled by the default `<a href>` click behavior
       * @see https://app.asana.com/0/226524042134185/1137183849467687/f
       */
      event.preventDefault()
    }
    this.props.onShare({ href, model, shareType })
  }

  // Component render methods

  renderCopyForm() {
    const { model } = this.props
    const { copied } = this.state
    return (
      tag('form', {
        className: bem.element('copy-form')
      },
        tag(InputText, {
          className: bem.element('copy-input'),
          readOnly: true,
          value: socialShareHref(model, 'copy')
        }),
        tag(CapsuleButton, {
          className: bem.element('copy-button', { copied }),
          onClick: this.onCopy
        },
          copied
          ? 'Copied!'
          : 'Copy link'
        )
      )
    )
  }

  renderFooter() {
    return (
      tag('footer', {
        className: bem.element('footer')
      },
        this.renderCopyForm()
      )
    )
  }

  renderHeader() {
    return (
      tag('header', {
        className: bem.element('header')
      },
        this.headerText()
      )
    )
  }

  renderContent() {
    const { model } = this.props
    const className = bem.element('content', {
      // Prefer the type enum key in the stylesheet instead of the value
      type: modelTypesKeys[model.type]
    })
    switch (model.type) {
      case modelTypes.ARTICLE: return (
        tag(ShareModalArticleContent, {
          className,
          article: model
        })
      )
      case modelTypes.MIX: return (
        tag(ShareModalCollectionContent, {
          className,
          collection: model
        })
      )
      case modelTypes.USER: return (
        tag(ShareModalUserContent, {
          className,
          user: model
        })
      )
    }
  }

  renderSocialButtons() {
    const { model, socialMediums } = this.props
    return (
      tag('div', {
        className: bem.element('hrefs-container')
      },
        socialMediums.map(medium => (
          tag(ShareModalShareButton, {
            key: medium.id,
            // Values
            className: bem.element('href'),
            href: socialShareHref(model, medium.id),
            name: medium.name,
            shareType: medium.id,
            // Callbacks
            onClick: this.onShare
          })
        ))
      )
    )
  }

  renderShareFriends() {
    return (
      tag(CheckRollout, {
        rolloutKey: 'web_internal_share',
        renderIncluded: () => (
          tag(ShareModalShareFriends, {
            className: bem.element('share-friends')
          })
        )
      })
    )
  }

  // Root render method

  render() {
    const { props } = this
    if (!props.model) {
      return null
    }
    return (
      tag(Modal, {
        className: classNames(
          props.className,
          bem.block()
        ),
        onDismiss: props.onDismiss
      },
        this.renderHeader(),
        this.renderContent(),
        this.renderSocialButtons(),
        this.renderFooter(),
        this.renderShareFriends()
      )
    )
  }
}

ShareModal.propTypes = {
  // Values
  className: PropTypes.string,
  model: PropTypes.oneOfType([
    PropTypes.shape(ArticleModelRecord.propTypes),
    PropTypes.shape(CollectionModelRecord.propTypes),
    PropTypes.shape(UserModelRecord.propTypes)
  ]),
  socialMediums: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    windowSize: PropTypes.array
  })).isRequired,
  // Callbacks
  onCopy: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired
}

module.exports = ShareModal
